import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

export type DSCircularButtonSize = "sm" | "md" | "lg" | "xxl";
export type DSCircularButtonVariant = "primary" | "danger";

export interface DSCircularButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: DSCircularButtonSize;
  variant?: DSCircularButtonVariant;
  /** Icon to display (FontAwesome IconProp) */
  icon?: IconProp;
  dataTestId: string;
  ref?: React.Ref<HTMLButtonElement>;
  children?: React.ReactNode;
}

export const DSCircularButton = ({
  size = "md",
  variant = "primary",
  icon,
  disabled = false,
  dataTestId,
  ref,
  type = "button",
  children,
  ...props
}: Omit<DSCircularButtonProps, "className">) => {
  // Base styles
  const baseStyles =
    "inline-flex items-center justify-center rounded-full transition-colors";

  // Variant styles
  const variantStyles = {
    primary:
      "bg-brand shadow-[0px_0px_10px_0px_var(--shadow-primary)] hover:bg-indigo-600",
    danger:
      "bg-danger shadow-[0px_0px_5px_0px_var(--shadow-danger)] hover:bg-red-600",
  };

  // State styles
  const stateStyles = disabled
    ? "bg-disabled opacity-80 shadow-[0px_0px_10px_0px_var(--shadow-secondary)] cursor-not-allowed"
    : variantStyles[variant];

  // Size styles - uses Tailwind numeric scale for larger spacing
  const sizeStyles = {
    sm: "size-8 p-sm", // 32px
    md: "size-12 p-sm", // 48px
    lg: "size-16 p-sm", // 64px
    xxl: "size-40 p-sm", // 160px
  };

  // Icon size styles
  const iconSizeStyles = {
    sm: "text-base", // 16x12px icon area
    md: "text-xl", // 24x18px icon area
    lg: "text-[32px]", // 32x24px icon area
    xxl: "text-[64px]", // 64x48px icon area
  };

  return (
    <button
      ref={ref}
      disabled={disabled}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      type={type}
      className={`${baseStyles} ${stateStyles} ${sizeStyles[size]}`}
      {...props}
    >
      {children
        ? children
        : icon && (
            <FontAwesomeIcon
              icon={icon}
              className={`text-white ${iconSizeStyles[size]}`}
            />
          )}
    </button>
  );
};

DSCircularButton.displayName = "DSCircularButton";
